import { Link } from 'react-router-dom'
import { Heart, Home as HomeIcon, Leaf, CalendarDays, ShieldCheck } from 'lucide-react'
import { Section, SectionHeading, IconCircle, CheckItem, FAQ, CTABand, SplitHero, NonMedicalNote } from '../components/ui'
import { PHONE_DISPLAY, PHONE_TEL } from '../lib/constants'

const services = [
  { icon: Heart, title: 'Personal Care Services', blurb: "Respectful help with bathing, dressing, grooming, mobility and daily reminders — always non-medical and always at your loved one's pace.", href: '/services/personal-care' },
  { icon: HomeIcon, title: 'Meal Preparation & Light Housekeeping', blurb: 'Home-cooked meals your loved one actually enjoys, plus tidy laundry, dishes and living spaces that feel calm and cared for.', href: '/services/meals-housekeeping' },
  { icon: Leaf, title: 'Companionship & Outings', blurb: 'Real conversation, shared hobbies, walks in the neighbourhood and visits out — the everyday connection that lifts a whole week.', href: '/services/companionship' },
  { icon: CalendarDays, title: 'Post-Hospital Care', blurb: 'Extra non-medical hands during the first weeks home — meals, rest, routines and reminders, while medical care stays with clinicians.', href: '/services/post-hospital-care' },
  { icon: ShieldCheck, title: 'Specialized Care', blurb: "Patient, familiar support for dementia, Alzheimer's, palliative, end-of-life and respite needs, alongside your healthcare team.", href: '/specialized-care' },
]

const mixItems = [
  'Blended care plans at no extra cost',
  'Adjust hours as needs change',
  'Caregiver matching based on personality',
  'Daily task logs in the Family Room',
]

const faqs = [
  { q: 'Can we combine multiple services into one visit?', a: 'Absolutely. Most of our visits include a mix of tasks—for example, a caregiver might help with a morning bath (Personal Care), tidy the kitchen (Housekeeping), and then share a walk and conversation (Companionship).' },
  { q: 'How do we know which services are right for us?', a: "We start with a free In-Home Assessment where we listen to your needs and suggest a plan. You're never pressured to sign up for more than you need." },
  { q: 'What if our needs change over time?', a: 'Plans are flexible. If you need more help after a surgery, or want to reduce hours during the summer, we simply update the care plan and the schedule in your Family Room portal.' },
]

export default function Services() {
  return (
    <>
      <SplitHero
        eyebrow="Care & Support"
        title="Services tailored to your family's needs"
        lede="We provide respectful, non-medical in-home care that helps Calgary seniors stay independent and comfortable. Every plan is built around the person, not a package."
        image="/assets/companionship.jpg"
        imageAlt="Caregiver and senior sharing a lighthearted moment at home"
        actions={
          <>
            <Link to="/schedule-assessment" className="btn btn-primary">Schedule an In-Home Assessment</Link>
            <a href={PHONE_TEL} className="btn btn-secondary">Call {PHONE_DISPLAY}</a>
          </>
        }
      />

      {/* Service cards */}
      <Section tone="panel">
        <SectionHeading
          eyebrow="What we offer"
          title="Ways we can help at home"
          lede="From an hour of companionship to daily personal care, our services are designed to be flexible and supportive."
        />
        <div className="grid grid-3" style={{ marginTop: 48, gap: 24 }}>
          {services.map((s) => (
            <div key={s.title} className="card-teal">
              <IconCircle icon={s.icon} variant="on-teal" />
              <h3 style={{ margin: '20px 0 0 0', fontSize: '1.5rem', lineHeight: 1.2, color: 'var(--color-cream)' }}>{s.title}</h3>
              <p style={{ margin: '12px 0 0 0', fontSize: 17, lineHeight: 1.6, color: 'oklch(0.9765 0.0098 87.47 / 85%)', flex: 1 }}>{s.blurb}</p>
              <Link to={s.href} className="link-inline link-inline-on-dark" style={{ marginTop: 24 }}>View service details</Link>
            </div>
          ))}
        </div>
      </Section>

      {/* Mixed services */}
      <Section tone="cream">
        <div className="grid grid-2" style={{ gap: 48 }}>
          <div style={{ minWidth: 0 }}>
            <SectionHeading
              title="Most families need a mix of services"
              lede="Care rarely fits into a single category. Your plan might include help with the morning routine, a fresh cooked lunch, and an afternoon of cards and conversation."
            />
            <div style={{ marginTop: 32 }}>
              <div className="grid" style={{ gap: 16, maxWidth: 920, width: '100%' }}>
                {mixItems.map((i) => <CheckItem key={i}>{i}</CheckItem>)}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', minWidth: 0, flexDirection: 'column', justifyContent: 'center' }}>
            <p style={{ margin: 0, fontSize: 19, lineHeight: 1.7, color: 'var(--color-muted)' }}>
              Independence looks different for everyone. As needs evolve—perhaps after a hospital stay or as a memory condition progresses—we adjust the plan alongside you. There are no lock-in contracts or rigid tiers; just the support you need, when you need it.
            </p>
            <div style={{ marginTop: 32 }}>
              <NonMedicalNote />
            </div>
          </div>
        </div>
      </Section>

      {/* FAQ */}
      <Section tone="panel-deep">
        <FAQ items={faqs} />
      </Section>

      <CTABand />
    </>
  )
}
